import { ITextInputProps, TextInput } from "@business-finland/wif-ui-lib";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useCallback, useMemo } from "react";

import { ONE_HOUR_IN_MS } from "../../../../constants/durations";
import useFormField from "../../../../hooks/useFormField";
import useQuery from "../../../../hooks/useQuery";
import { clsx } from "../../../../util/clsx";
import { companyFormDefaultText, companyIdRegex } from "../constants";
import { getCompanyFormStyleClassName, useFormFieldChecks } from "../helpers";
import { useCompanyFormDispatch, companyFormActions } from "../store";
import CompanyFieldset from "./Fieldset";

const HtmlParser = dynamic(() => import("../../../../components/HtmlParser"));

const fieldId = "companyId";

export interface ICompanyIdFieldProps {
  heading: string;
  value?: string;
  readOnly?: boolean;
  unmetCriteriaErrorMessage?: string;
  labelText?: string;
  placeholderText?: string;
}

export default function CompanyIdField({
  value: defaultValue,
  readOnly,
  unmetCriteriaErrorMessage,
  heading,
  labelText = companyFormDefaultText.companyIdLabel,
  placeholderText = companyFormDefaultText.companyIdPlaceholder,
}: ICompanyIdFieldProps) {
  const { t } = useTranslation();
  const dispatch = useCompanyFormDispatch();

  const { value, errors, inputProps, resetFieldState } =
    useCompanyIdFormField(defaultValue);

  const { isCompanyIdApproved, isCompanyIdChecked, isCompanyIdChecking } =
    useCompanyIdValidateQuery(errors.length ? undefined : value, readOnly);

  const iconState: ITextInputProps["iconState"] = useMemo(() => {
    if (isCompanyIdApproved || readOnly) return "CHECK";
    return value ? "CLEAR" : "SEARCH";
  }, [value, isCompanyIdApproved, readOnly]);

  const handleIconClick = useCallback(() => {
    resetFieldState();
    inputProps.ref.current?.focus();
    dispatch(companyFormActions.resetCompanyId());
  }, [resetFieldState, dispatch, inputProps.ref]);

  const isUnmetCriteriaErrorMessageVisible = Boolean(
    !readOnly &&
      isCompanyIdChecked &&
      !isCompanyIdChecking &&
      !isCompanyIdApproved &&
      unmetCriteriaErrorMessage
  );

  return (
    <CompanyFieldset id={fieldId} heading={heading}>
      <TextInput
        label={labelText}
        placeholder={placeholderText}
        required
        iconState={iconState}
        onIconClick={iconState !== "CHECK" ? handleIconClick : undefined}
        readOnly={readOnly}
        autoComplete="off"
        helpText={isCompanyIdChecking ? t("form.verifying") : undefined}
        {...inputProps}
      />

      {isUnmetCriteriaErrorMessageVisible ? (
        <div
          className={clsx(
            "texts__lead_text",
            getCompanyFormStyleClassName(fieldId, "error")
          )}
        >
          <HtmlParser html={unmetCriteriaErrorMessage} />
        </div>
      ) : null}
    </CompanyFieldset>
  );
}

function useCompanyIdFormField(defaultValue?: string) {
  const dispatch = useCompanyFormDispatch();

  const checks = useFormFieldChecks(true, companyIdRegex);

  const handleFieldError = useCallback(
    () => dispatch(companyFormActions.resetCompanyId()),
    [dispatch]
  );

  return useFormField({
    name: fieldId,
    defaultValue,
    checks,
    onError: handleFieldError,
  });
}

type CompanyIdValidationResponse = {
  companyId: string;
  companyName?: string;
};

function useCompanyIdValidateQuery(
  companyId: string | undefined,
  readOnly?: boolean
) {
  const { locale } = useRouter();
  const dispatch = useCompanyFormDispatch();
  const [isCompanyIdApproved, setIsCompanyIdApproved] = useState(false);

  const handleQuerySuccess = useCallback(
    (data: CompanyIdValidationResponse) => {
      setIsCompanyIdApproved(true);
      dispatch(companyFormActions.setCompanyId(data));
    },
    [dispatch]
  );

  const { isDisabled, isLoading } = useQuery<CompanyIdValidationResponse>(
    `/api/companies/validate-id/?companyId=${companyId}&locale=${locale}`,
    {
      disabled: !companyId || readOnly,
      cacheDurationInMs: ONE_HOUR_IN_MS,
      onSuccess: handleQuerySuccess,
    }
  );

  return {
    isCompanyIdApproved: Boolean(companyId && isCompanyIdApproved),
    isCompanyIdChecked: !isDisabled,
    isCompanyIdChecking: isLoading,
  };
}
